import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  MapPin, 
  Filter, 
  RefreshCw,
  Flame,
  AlertTriangle,
  Camera
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

// Custom fire icon for map markers
const fireIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#E85D04" width="24" height="24">
      <path d="M12 2C13.1 2 14 2.9 14 4C14 5.3 13.1 6.1 12.5 6.5C12.2 6.7 12 7 12 7.3V8H13C14.1 8 15 8.9 15 10V11C15 12.1 14.1 13 13 13H11C9.9 13 9 12.1 9 11V10C9 8.9 9.9 8 11 8H12V7.3C12 6.5 12.4 5.8 13 5.3C13.6 4.8 14 4.1 14 3.3C14 2.6 13.4 2 12.7 2H12Z"/>
      <path d="M8.5 14.5C8.5 16.4 10.1 18 12 18S15.5 16.4 15.5 14.5C15.5 13.1 14.6 11.9 13.3 11.3C12.9 11.1 12.5 11 12 11S11.1 11.1 10.7 11.3C9.4 11.9 8.5 13.1 8.5 14.5Z"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const MapPage = () => {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    district: '',
    status: '',
    active_only: true,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Initialize filters from URL parameters
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const districtParam = searchParams.get('district');
    
    if (statusParam || districtParam) {
      setFilters(prev => ({
        ...prev,
        status: statusParam || '',
        district: districtParam || ''
      }));
    }
  }, [searchParams]);

  // Fetch fires with filters
  const { data: firesData, isLoading, refetch } = useQuery(
    ['map-fires', filters],
    () => apiService.getFires(filters),
    { refetchInterval: 30000 }
  );

  // Fetch districts for filter
  const { data: districtsData } = useQuery(
    'districts',
    () => apiService.getDistricts()
  );

  // Fetch statuses for filter
  const { data: statusesData } = useQuery(
    'statuses',
    () => apiService.getStatuses()
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
    toast.success('Mapa atualizado!');
  };

  const handleSync = async () => {
    if (!isAdmin()) return;
    
    try {
      setIsRefreshing(true);
      await apiService.syncFires();
      await refetch();
      toast.success('Sincronização concluída!');
    } catch (error) {
      toast.error('Erro na sincronização');
    } finally {
      setIsRefreshing(false);
    }
  };

  const updateFilters = (newFilters) => {
    setFilters(newFilters);
    
    // Update URL parameters
    const params = new URLSearchParams();
    if (newFilters.district) params.set('district', newFilters.district);
    if (newFilters.status) params.set('status', newFilters.status);
    setSearchParams(params);
  };

  const getStatusBadge = (fire) => {
    if (fire.is_important) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Importante
        </span>
      );
    }
    if (fire.is_active) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
          Ativo
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
        Inativo
      </span>
    );
  };

  const fires = firesData?.fires || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Mapa de Incêndios
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Visualização geográfica dos incêndios florestais
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </button>
          {isAdmin() && (
            <button
              onClick={handleSync}
              disabled={isRefreshing}
              className="btn-primary flex items-center space-x-2"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span>Sincronizar</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center space-x-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Distrito
              </label>
              <select
                value={filters.district}
                onChange={(e) => updateFilters({ ...filters, district: e.target.value })}
                className="form-input w-48"
              >
                <option value="">Todos os distritos</option>
                {districtsData?.districts?.map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Estado
              </label>
              <select
                value={filters.status}
                onChange={(e) => updateFilters({ ...filters, status: e.target.value })}
                className="form-input w-48"
              >
                <option value="">Todos os estados</option>
                {statusesData?.statuses?.map((status) => (
                  <option key={status.status} value={status.status}>
                    {status.status} ({status.count})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="active-only-map"
                checked={filters.active_only}
                onChange={(e) => updateFilters({ ...filters, active_only: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="active-only-map" className="text-sm text-gray-700 dark:text-gray-300">
                Apenas ativos
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="card p-0 overflow-hidden">
        <div className="h-96 lg:h-[600px]">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <MapContainer
              center={[39.5, -8.0]} // Center of Portugal
              zoom={7}
              style={{ height: '100%', width: '100%' }}
              className="z-0"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {fires.map((fire) => {
                if (!fire.lat || !fire.lng) return null;
                
                return (
                  <Marker
                    key={fire.id}
                    position={[fire.lat, fire.lng]}
                    icon={fireIcon}
                  >
                    <Popup className="fire-popup">
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 min-w-[280px]">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-base mb-1">
                              {fire.location}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {fire.district}
                              {fire.concelho && ` • ${fire.concelho}`}
                            </p>
                          </div>
                          <div className="ml-2">
                            {getStatusBadge(fire)}
                          </div>
                        </div>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                            <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <span>{fire.natureza || 'N/A'}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                            <Camera className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <span>{fire.photo_count} fotos</span>
                          </div>
                          {fire.status && (
                            <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
                              <div 
                                className="w-3 h-3 rounded-full border border-gray-300 dark:border-gray-600"
                                style={{ backgroundColor: `#${fire.status_color || 'CCCCCC'}` }}
                              ></div>
                              <span className="font-medium">{fire.status}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex space-x-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                          <Link
                            to={`/fires/${fire.id}`}
                            className="flex-1 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium px-3 py-2 rounded-md transition-colors duration-200 text-center"
                          >
                            Ver Detalhes
                          </Link>
                          <Link
                            to={`/upload?fire_id=${fire.id}`}
                            className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium px-3 py-2 rounded-md transition-colors duration-200 text-center"
                          >
                            Enviar Foto
                          </Link>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-fire-100 rounded-lg">
              <Flame className="w-5 h-5 text-fire-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Incêndios no Mapa
              </p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {fires.length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Ativos
              </p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {fires.filter(f => f.is_active).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Camera className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total de Fotos
              </p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {fires.reduce((sum, fire) => sum + (fire.photo_count || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapPage;
